import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { SalesStatistics } from '../../../../types';
import { formatCurrency } from '../../../../../../../../utils/functions';
import { localDB } from '../../../../../../../../crud/localDB/localDB';

interface GraficoVentasProps {
  salesData: SalesStatistics[];
}

interface AxisData {
  date: Date;
  label: string;
  startDate?: string;
  endDate?: string;
}

const GraficoVentas: React.FC<GraficoVentasProps> = ({ salesData }) => {
  // ─── State Hooks ────────────────────────────────────────────────────────────
  const [xAxisData, setXAxisData] = useState<AxisData[]>([]);
  const [colorMode, setColorMode] = useState<string>('dark');
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  // New: computed view window and data notice
  const [viewStart, setViewStart] = useState<Date | null>(null);
  const [viewEnd, setViewEnd] = useState<Date | null>(null);
  const [dataNotice, setDataNotice] = useState<string | null>(null);

  // Calculate view range based on filtered data from parent
  useEffect(() => {
    if (!salesData.length) {
      setViewStart(null);
      setViewEnd(null);
      setDataNotice("No hay datos para mostrar en el rango seleccionado.");
      return;
    }

    const startOfDay = (d: Date) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };

    const endOfDay = (d: Date) => {
      const x = new Date(d);
      x.setHours(23, 59, 59, 999);
      return x;
    };

    // Use the actual data range since filtering is handled by parent CompactFilterBar
    const minDataDate = salesData.reduce((min, rec) => {
      const d = new Date(rec.end_timestamp);
      return d < min ? d : min;
    }, new Date(salesData[0].end_timestamp));

    const maxDataDate = salesData.reduce((max, rec) => {
      const d = new Date(rec.end_timestamp);
      return d > max ? d : max;
    }, new Date(salesData[0].end_timestamp));

    setViewStart(startOfDay(minDataDate));
    setViewEnd(endOfDay(maxDataDate));
    setDataNotice(null); // Clear any previous notices
  }, [salesData]);

  // ─── Listen for window‐resize to update isMobile ─────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ─── Load colorMode from LocalDB (for dark/light) ───────────────────────────
  useEffect(() => {
    const loadColorMode = async () => {
      try {
        const mode = await localDB.get<string>('ui', 'colorMode');
        if (mode) {
          setColorMode(mode);
        }
      } catch (error) {
        console.error('Error loading color mode from LocalDB:', error);
        setColorMode('dark'); // fallback
      }
    };
    
    loadColorMode();
  }, []);

  // ─── Determine text/background colors based on colorMode ─────────────────────
  const textColor = colorMode === 'light' ? '#6b6b6b' : '#FFFFFF';
  const backgroundColor = 'transparent';

  // Build X-axis buckets accurately per requirement
  useEffect(() => {
    if (!viewStart || !viewEnd) {
      setXAxisData([]);
      return;
    }

    const arr: AxisData[] = [];

    // Determine axis type based on data range
    const dayMs = 24 * 60 * 60 * 1000;
    const totalDays = Math.floor((viewEnd.getTime() - viewStart.getTime()) / dayMs) + 1;

    if (totalDays <= 7) {
      // 7 daily buckets with weekday letters for last 7 days
      const dayNames = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
      const d = new Date(viewStart);
      for (let i = 0; i < 7; i++) {
        const curr = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
        const weekday = curr.getDay();
        arr.push({
          date: curr,
          label: dayNames[weekday],
        });
      }
    } else if (totalDays <= 31) {
      // 30 daily buckets (oldest to newest)
      const dayMs = 24 * 60 * 60 * 1000;
      const totalDays = Math.min(30, Math.floor((viewEnd.getTime() - viewStart.getTime()) / dayMs) + 1);
      for (let i = 0; i < totalDays; i++) {
        const curr = new Date(viewStart.getFullYear(), viewStart.getMonth(), viewStart.getDate() + i);
        arr.push({
          date: curr,
          label: curr.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
        });
      }
    } else {
      // Monthly buckets for longer periods
      const startMonth = new Date(viewStart.getFullYear(), viewStart.getMonth(), 1);
      const endMonth = new Date(viewEnd.getFullYear(), viewEnd.getMonth(), 1);
      
      let current = new Date(startMonth);
      while (current <= endMonth) {
        arr.push({
          date: new Date(current),
          label: current.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }),
          startDate: new Date(current.getFullYear(), current.getMonth(), 1).toISOString().split('T')[0],
          endDate: new Date(current.getFullYear(), current.getMonth() + 1, 0).toISOString().split('T')[0],
        });
        current.setMonth(current.getMonth() + 1);
      }
    }

    setXAxisData(arr);
  }, [viewStart, viewEnd]);

  // Filter data by the computed window
  const filteredData = salesData.filter((data) => {
    const saleDate = new Date(data.end_timestamp);
    if (viewStart && viewEnd) {
      const start = new Date(viewStart);
      start.setHours(0, 0, 0, 0);
      const endD = new Date(viewEnd);
      endD.setHours(23, 59, 59, 999);
      return saleDate >= start && saleDate <= endD;
    }
    return true;
  });

  // Calculate total days for chart formatting
  const totalDays = viewStart && viewEnd 
    ? Math.floor((viewEnd.getTime() - viewStart.getTime()) / (24 * 60 * 60 * 1000)) + 1 
    : 0;

  // ─── Helper: Aggregate a given numeric field over each X‐axis bucket ────────
  const createSeriesData = (field: keyof SalesStatistics) =>
    xAxisData.map((axis) => {
      let relevantData: SalesStatistics[] = [];
      if (axis.startDate && axis.endDate) {
        const start = axis.startDate;
        const end = axis.endDate;
        relevantData = filteredData.filter((data) => {
          const dStr = new Date(data.end_timestamp).toISOString().split('T')[0];
          return dStr >= start && dStr <= end;
        });
      } else {
        relevantData = filteredData.filter((data) => {
          const d = new Date(data.end_timestamp);
          return (
            d.getDate() === axis.date.getDate() &&
            d.getMonth() === axis.date.getMonth() &&
            d.getFullYear() === axis.date.getFullYear()
          );
        });
      }
      return relevantData.reduce(
        (sum, rec) =>
          sum + (isNaN((rec[field] as unknown as number) ?? 0)
            ? 0
            : (rec[field] as unknown as number)),
        0
      );
    });

  // ─── Build four series arrays: grossSales, netSales, totalTip, netLoss ─────
  const seriesNames = [
    'Bruto',
    'Neto',
    'Propinas',
    'Pérdidas',
  ];
  const grossSales = createSeriesData('gross_sales');
  const netSales = createSeriesData('net_sales');
  const totalTip = createSeriesData('total_tip');
  const netLoss = createSeriesData('net_loss');
  const seriesData = [grossSales, netSales, totalTip, netLoss];

  const axisLabelFormatter = (value: string, index: number) => {
    if (totalDays <= 31) {
      const len = xAxisData.length;
      if (index === 0 || index === Math.floor(len / 2) || index === len - 1) {
        return value;
      }
      return '';
    }
    return value;
  };

  // ─── Enhanced color palette with modern gradients ──────────────────────────
  const CHART_COLORS = [
    '#FC7556', // Ventas Brutas (brand orange)
    '#E85B44', // Ventas Netas (darker orange)
    '#22c55e', // Propinas (modern green)
    '#ef4444', // Pérdidas Netas (modern red)
  ];

  // ─── Configure Modern Line Chart Options ────────────────────────────────────
  const lineOption = {
    color: CHART_COLORS,
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 1200,
    animationEasing: 'cubicInOut' as const,
    animationDelayUpdate: 300,
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: {
        type: 'cross' as const,
        lineStyle: {
          color: '#FC7556',
          width: 2,
          type: 'dashed' as const
        },
        crossStyle: {
          color: '#FC7556'
        },
        label: {
          backgroundColor: '#FC7556',
          color: '#ffffff',
          fontWeight: 600,
          borderRadius: 6,
          padding: [8, 12]
        }
      },
      backgroundColor: colorMode === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(249, 250, 251, 0.95)',
      borderColor: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.3)',
      borderWidth: 1,
      borderRadius: 12,
      textStyle: {
        color: textColor,
        fontWeight: 500,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      padding: [12, 16],
      boxShadow: colorMode === 'dark' 
        ? '0 10px 25px rgba(0, 0, 0, 0.3)' 
        : '0 10px 25px rgba(0, 0, 0, 0.1)',
      formatter: function(params: any) {
        if (!Array.isArray(params)) return '';
        
        let html = `<div style="font-weight: 600; margin-bottom: 8px; color: ${textColor};">${params[0].axisValue}</div>`;
        params.forEach((param: any) => {
          const color = param.color;
          html += `
            <div style="display: flex; align-items: center; margin: 6px 0;">
              <span style="
                display: inline-block; 
                width: 12px; 
                height: 12px; 
                border-radius: 50%; 
                background: ${color}; 
                margin-right: 8px;
                box-shadow: 0 0 0 2px ${backgroundColor};
              "></span>
              <span style="font-weight: 500;">${param.seriesName}: </span>
              <span style="font-weight: 700;">${formatCurrency(param.value)}</span>
            </div>
          `;
        });
        return html;
      }
    },
    legend: {
      data: seriesNames,
      top: 15,
      left: 'center',
      textStyle: { 
        color: textColor,
        fontWeight: 600,
        fontSize: 12,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      bottom: isMobile ? 0 : 'auto',
      orient: 'horizontal' as const,
      itemWidth: 12,
      itemHeight: 12,
      itemGap: isMobile ? 12 : 20,
      icon: 'circle'
    },
    xAxis: {
      type: 'category' as const,
      data: xAxisData.map((item) => item.label),
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)',
          width: 1
        }
      },
      axisTick: { show: false },
      axisLabel: {
        color: textColor,
        rotate: totalDays <= 31 ? 0 : (isMobile ? 30 : 0),
        fontSize: isMobile ? 10 : 11,
        fontWeight: 500,
        margin: 12,
        formatter: axisLabelFormatter as any,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      }
    },
    yAxis: {
      type: 'value' as const,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: textColor,
        fontSize: 11,
        fontWeight: 500,
        margin: 12,
        formatter: (val: number) => formatCurrency(val),
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      splitLine: {
        lineStyle: {
          color: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.6)',
          type: 'dashed' as const,
          width: 1
        }
      }
    },
    grid: {
      left: '4%',
      right: '4%',
      bottom: isMobile ? '15%' : '12%',
      top: '20%',
      containLabel: true
    },
    series: seriesData.map((data, idx) => ({
      name: seriesNames[idx],
      type: 'line' as const,
      data,
      smooth: 0.3,
      connectNulls: true,
      lineStyle: {
        width: 3.5,
        color: CHART_COLORS[idx],
        shadowColor: `${CHART_COLORS[idx]}40`,
        shadowBlur: 8,
        shadowOffsetY: 2
      },
      areaStyle: {
        color: new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${CHART_COLORS[idx]}25` },
          { offset: 0.7, color: `${CHART_COLORS[idx]}10` },
          { offset: 1, color: `${CHART_COLORS[idx]}05` }
        ])
      },
      itemStyle: {
        color: CHART_COLORS[idx],
        borderWidth: 3,
        borderColor: backgroundColor,
        shadowColor: `${CHART_COLORS[idx]}60`,
        shadowBlur: 6
      },
      emphasis: {
        scale: true,
        scaleSize: 12,
        itemStyle: { shadowBlur: 10, shadowColor: `${CHART_COLORS[idx]}80` }
      },
      symbolSize: 8,
      symbol: 'circle'
    }))
  };

  // ─── Build Pie Data (net sales by day of week) ──────────────────────────────
  const salesByDay: Record<number, number> = {};
  for (let i = 1; i <= 7; i++) salesByDay[i] = 0;
  filteredData.forEach((rec) => {
    const day = rec.day_number;
    const netSale = isNaN(rec.net_sales) ? 0 : rec.net_sales;
    if (salesByDay.hasOwnProperty(day)) {
      salesByDay[day] += netSale;
    }
  });
  const dayLabels: Record<number, string> = {
    1: 'Lunes',
    2: 'Martes',
    3: 'Miércoles',
    4: 'Jueves',
    5: 'Viernes',
    6: 'Sábado',
    7: 'Domingo',
  };
  const pieData = Object.entries(salesByDay)
    .filter(([_, value]) => value > 0)
    .map(([day, value]) => ({
      name: dayLabels[Number(day)] || `Día ${day}`,
      value,
    }));

  // ─── Configure Modern Pie Chart Options ─────────────────────────────────────
  const pieOption = {
    color: CHART_COLORS,
    backgroundColor: 'transparent',
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut' as const,
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: colorMode === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(249, 250, 251, 0.95)',
      borderColor: colorMode === 'dark' ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.3)',
      borderWidth: 1,
      borderRadius: 12,
      textStyle: {
        color: textColor,
        fontWeight: 500,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      padding: [12, 16],
      boxShadow: colorMode === 'dark' 
        ? '0 10px 25px rgba(0, 0, 0, 0.3)' 
        : '0 10px 25px rgba(0, 0, 0, 0.1)',
      formatter: function(params: any) {
        return `
          <div style="font-weight: 600; margin-bottom: 6px; color: ${textColor};">
            ${params.name}
          </div>
          <div style="display: flex; align-items: center;">
            <span style="
              display: inline-block; 
              width: 12px; 
              height: 12px; 
              border-radius: 50%; 
              background: ${params.color}; 
              margin-right: 8px;
              box-shadow: 0 0 0 2px ${backgroundColor};
            "></span>
            <span style="font-weight: 700;">
              ${formatCurrency(params.value)} (${params.percent}%)
            </span>
          </div>
        `;
      }
    },
    legend: !isMobile ? {
      orient: 'vertical' as const,
      left: 15,
      top: 'center' as const,
      textStyle: { 
        color: textColor,
        fontWeight: 600,
        fontSize: 12,
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      },
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 12,
      icon: 'circle' as const
    } : undefined,
    series: [
      {
        name: 'Ventas netas por día',
        type: 'pie' as const,
        radius: ['45%', '75%'],
        center: isMobile ? ['50%', '50%'] : ['65%', '50%'],
        avoidLabelOverlap: false,
        padAngle: 2,
        itemStyle: {
          borderRadius: 6,
          borderColor: backgroundColor,
          borderWidth: 3,
          shadowBlur: 8,
          shadowColor: 'rgba(0, 0, 0, 0.2)'
        },
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 700,
            color: textColor,
            formatter: '{b}\n{d}%',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          },
          scale: true,
          scaleSize: 8,
          itemStyle: {
            shadowBlur: 15,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          }
        },
        labelLine: {
          show: false
        },
        data: pieData.map((item, index) => ({
          value: item.value,
          name: item.name,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
              { offset: 0, color: CHART_COLORS[index % CHART_COLORS.length] },
              { offset: 1, color: `${CHART_COLORS[index % CHART_COLORS.length]}CC` }
            ])
          }
        }))
      }
    ]
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {dataNotice && (
        <div className="text-sm text-center py-2 rounded-lg mb-3" style={{ color: 'var(--text-color)', opacity: 0.6 }}>{dataNotice}</div>
      )}

      {filteredData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="mp-chart-card lg:col-span-3">
            <div className="mp-chart-header">Tendencia de Ventas</div>
            <div className="mp-chart-body" style={{ height: isMobile ? 300 : 420 }}>
              <ReactECharts option={lineOption} style={{ height: '100%' }} />
            </div>
          </div>
          <div className="mp-chart-card lg:col-span-2">
            <div className="mp-chart-header">Distribución por Día</div>
            <div className="mp-chart-body" style={{ height: isMobile ? 300 : 420 }}>
              <ReactECharts option={pieOption} style={{ height: '100%' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraficoVentas;
